import { Box, Divider, FormHelperText, Stack, Typography } from "@mui/material";
import { DistaMarkdownEditor } from "@sista/dista-ui";
import { useRequiredValidation } from "@sista/library-stasi";
import {
  GridRowSelectionModel,
  OrganizationProjectList,
  SkeletonProjectData,
} from "@sista/service-project-skeleton";
import { SistaChip } from "@sista/sista-chip";
import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { TempFilesElement } from "./../../../components/ui/TempFilesElement";

export type NoticeFormData = {
  subject: string;
  description: string;
  justification: string;
  projectsAffected: boolean;
  impacts?: string;
  fileIds?: string[];
};

type OrganizationChangeNoticeFormFieldsProps = {
  form: UseFormReturn<NoticeFormData>;
  organizationId: string;
  authKey: string;
  selectedProjects: SkeletonProjectData[];
  onProjectsChange: (projects: SkeletonProjectData[]) => void;
  projectsError: boolean;
  initialProjectIds?: string[];
  hasInteractedRef?: React.MutableRefObject<boolean>;
  fistoContext?: string[];
};

export const OrganizationChangeNoticeFormFields = ({
  form,
  organizationId,
  authKey,
  selectedProjects,
  onProjectsChange,
  projectsError,
  initialProjectIds = [],
  hasInteractedRef,
  fistoContext,
}: OrganizationChangeNoticeFormFieldsProps) => {
  const { t } = useTranslation("ucet");
  const required = useRequiredValidation();

  const projectsAffected = form.watch("projectsAffected");

  const isInteracted = hasInteractedRef?.current ?? false;
  const chipIds =
    isInteracted || initialProjectIds.length === 0
      ? selectedProjects.map((p) => p.id)
      : initialProjectIds;
  const rowSelectionModel: GridRowSelectionModel =
    isInteracted || initialProjectIds.length === 0
      ? selectedProjects.map((p) => p.id)
      : (initialProjectIds as GridRowSelectionModel);

  return (
    <>
      <TextFieldElement
        name="subject"
        label={t("ucet.organizationChangeNotice.subject", "Předmět změny")}
        validation={{ ...required }}
        fullWidth
      />
      <Controller
        name="description"
        control={form.control}
        rules={{
          validate: (value) =>
            (!!value && value.trim() !== "") || (required.required as string),
        }}
        render={({ field, fieldState }) => (
          <DistaMarkdownEditor
            defaultValue={field.value ?? ""}
            onChange={field.onChange}
            label={t(
              "ucet.organizationChangeNotice.description",
              "Popis požadované nebo ohlašované změny",
            )}
            required
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="justification"
        control={form.control}
        rules={{
          validate: (value) =>
            (!!value && value.trim() !== "") || (required.required as string),
        }}
        render={({ field, fieldState }) => (
          <DistaMarkdownEditor
            defaultValue={field.value ?? ""}
            onChange={field.onChange}
            label={t(
              "ucet.organizationChangeNotice.justification",
              "Zdůvodnění změny",
            )}
            required
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <CheckboxElement
        name="projectsAffected"
        label={t(
          "ucet.organizationChangeNotice.projectsAffected",
          "Změna má dopad na projekty",
        )}
      />
      {projectsAffected && (
        <>
          <Controller
            name="impacts"
            control={form.control}
            rules={{
              validate: (value) =>
                !projectsAffected ||
                (!!value && value.trim() !== "") ||
                (required.required as string),
            }}
            render={({ field, fieldState }) => (
              <DistaMarkdownEditor
                defaultValue={field.value ?? ""}
                onChange={field.onChange}
                label={t(
                  "ucet.organizationChangeNotice.impacts",
                  "Dopady změny do projektů",
                )}
                required
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Stack spacing={1}>
            <Typography variant="h6">
              {t(
                "ucet.organizationChangeNotice.provider.affectedProjects",
                "Vybrané projekty",
              )}
            </Typography>
            {chipIds.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {chipIds.map((id) => (
                  <SistaChip
                    key={id}
                    authToken={authKey}
                    urn={"urn:sista:projects:" + id}
                    variant="tiny"
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  "ucet.organizationChangeNotice.noProjectsSelected",
                  "Není vybrán žádný projekt.",
                )}
              </Typography>
            )}
          </Stack>
          <Box sx={{ height: 400 }}>
            <OrganizationProjectList
              filter={{
                organizationId,
                projectState: ["200", "300", "400", "500", "600", "700"],
              }}
              displayColumns={["roCfpId", "callForProposal", "projectSubState"]}
              checkboxSelection
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(
                _model: GridRowSelectionModel,
                selected: SkeletonProjectData[],
              ) => {
                if (hasInteractedRef) {
                  hasInteractedRef.current = true;
                }
                onProjectsChange(selected);
              }}
            />
          </Box>
          {projectsError && (
            <FormHelperText error>
              {t(
                "ucet.organizationChangeNotice.projectsRequired",
                "Vyberte alespoň jeden projekt.",
              )}
            </FormHelperText>
          )}
        </>
      )}
      {fistoContext && (
        <>
          <Divider />
          <Typography variant="h6">
            {t("ucet.organizationChangeNotice.attachments", "Přílohy")}
          </Typography>
          <TempFilesElement
            name={"fileIds"}
            context={fistoContext}
            multiple={true}
            label={t(
              "ucet.organizationChangeNotice.addAttachment",
              "Vložit přílohy",
            )}
          />
        </>
      )}
    </>
  );
};
